import type { ReactElement } from "react";

import { ArticleHero } from "../ArticleHero";
import maternityHeroImage from "../../assets/images/maternity-outdoor-fall-red-gown investment.webp";
import maternityBlackDressImage from "../../assets/images/maternity-studio-black-dress-portrait.webp";
import maternityWinterDressImage from "../../assets/images/maternity-winter-purple-lace-dress.webp";
import { ArticlePhoto } from "./ArticlePhoto";
import { UiIcon } from "./components/UiIcons";
import { GuideCtaActions } from "./components/GuideCtaActions";
import "./NewbornPages.scss";
import "./NewbornArticle.scss";

const palettes = [
  { name: "Warm neutrals", colors: ["#f2e7d9", "#c9aa91", "#80685a"] },
  { name: "Soft earth", colors: ["#d8c3a5", "#a6785d", "#66705c"] },
  { name: "Muted romance", colors: ["#ead4d1", "#b88c88", "#735e68"] },
];

export const WhatToWearForMaternityPhotos = (): ReactElement => (
  <main className="newborn-page newborn-article-page maternity-article-page">
    <article>
      <ArticleHero
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Resources", to: "/resources/" },
          { label: "Maternity", to: "/resources/maternity/" },
          { label: "What to Wear for Maternity Photos" },
        ]}
        category="Maternity styling guide"
        description="Simple outfit ideas that flatter your bump, move beautifully and keep the photographs feeling timeless."
        imageAlt="Expectant mother wearing a soft flowing dress for maternity photographs"
        imagePosition="center 43%"
        imageSrc={maternityHeroImage}
        readingTime="6 min read"
        title="What to Wear for Maternity Photos"
        updatedAt="2026-08-04"
        updatedLabel="Updated August 2026"
      />

      <div className="newborn-shell newborn-article-layout">
        <aside className="newborn-article-callout">
          <span className="newborn-article-callout__icon">
            <UiIcon name="star" />
          </span>
          <div>
            <h2>Quick answer</h2>
            <p>
              Choose something comfortable that defines your bump: a fitted dress, a flowing gown with shape at the
              waist, or a simple top with a long skirt. Soft, muted colors and natural textures keep attention on you
              and photograph beautifully.
            </p>
          </div>
        </aside>

        <nav aria-label="In this guide" className="newborn-article-toc">
          <h2>In this guide</h2>
          <ol>
            <li>
              <a href="#silhouette">Choose your silhouette</a>
            </li>
            <li>
              <a href="#palette">Color palette ideas</a>
            </li>
            <li>
              <a href="#texture">Texture and movement</a>
            </li>
            <li>
              <a href="#family">Coordinate your family</a>
            </li>
            <li>
              <a href="#avoid">What to avoid</a>
            </li>
            <li>
              <a href="#bring">What to bring</a>
            </li>
          </ol>
        </nav>

        <section className="newborn-article-section newborn-article-split" id="silhouette">
          <div>
            <p className="newborn-article-kicker">START WITH SHAPE</p>
            <h2>Choose a silhouette that celebrates your bump</h2>
            <p>
              Fitted knit dresses show the curve of your bump clearly. Flowing dresses add softness and movement,
              especially when they are fitted above or just below the bump. An open cardigan, robe or unbuttoned shirt
              can create variety without a full outfit change.
            </p>
            <p>
              The best choice is one you can sit, walk and breathe in comfortably. Try it on before the session and look
              at it from the side as well as the front.
            </p>
          </div>
          <ArticlePhoto
            alt="Flowing maternity dress with a defined waist"
            className="newborn-article-split__photo"
            label="PHOTO: SILHOUETTE"
            orientation="portrait"
            src={maternityBlackDressImage}
          />
        </section>

        <section className="newborn-article-section" id="palette">
          <p className="newborn-article-kicker">A SOFT, TIMELESS BASE</p>
          <h2>Three easy color palettes</h2>
          <p>
            You do not need to wear beige, but colors that are slightly softened tend to keep skin tones natural and let
            connection lead the image.
          </p>
          <div className="maternity-palette-grid">
            {palettes.map((palette) => (
              <article key={palette.name}>
                <div>
                  {palette.colors.map((color) => (
                    <i key={color} style={{ backgroundColor: color }} />
                  ))}
                </div>
                <h3>{palette.name}</h3>
              </article>
            ))}
          </div>
        </section>

        <section className="newborn-article-section" id="texture">
          <p className="newborn-article-kicker">ADD DEPTH</p>
          <h2>Use texture and movement thoughtfully</h2>
          <p>
            Linen, fine knits, gauze, lace and matte fabrics create depth without competing with your face. A skirt or
            dress with movement gives your hands something natural to do and brings softness to walking photographs.
          </p>
          <p>
            If a fabric wrinkles easily or becomes transparent in backlight, test it near a window before photo day and
            bring the right seamless undergarments.
          </p>
        </section>

        <section className="newborn-article-section newborn-article-split newborn-article-split--reverse" id="family">
          <ArticlePhoto
            alt="Pregnant woman wearing a purple lace gown for an outdoor winter maternity session"
            className="newborn-article-split__photo"
            label="PHOTO: FAMILY LOOKS"
            orientation="portrait"
            src={maternityWinterDressImage}
          />
          <div>
            <p className="newborn-article-kicker">COORDINATE, DO NOT MATCH</p>
            <h2>Dress your partner and children around your look</h2>
            <p>
              Choose your outfit first, then repeat one or two tones in everyone else’s clothing. Mix light and medium
              shades rather than putting the whole family in exactly the same color.
            </p>
            <p>
              Simple shirts, soft trousers and dresses without large graphics work well. Let one person wear a subtle
              pattern while the remaining outfits stay quiet.
            </p>
          </div>
        </section>

        <section className="newborn-article-section" id="avoid">
          <p className="newborn-article-kicker">KEEP THE FOCUS ON YOU</p>
          <h2>What is usually best to avoid</h2>
          <ul className="newborn-article-checklist">
            <li>
              <UiIcon name="check" />
              <span>Neon colors, large logos and very high-contrast prints.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Stiff pieces that restrict sitting, walking or natural hand placement.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Busy accessories that need constant adjusting.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Brand-new shoes that are uncomfortable for walking.</span>
            </li>
          </ul>
        </section>
        <section className="newborn-article-section" id="bring">
          <p className="newborn-article-kicker">A SMALL BACKUP PLAN</p>
          <h2>What to bring on photo day</h2>
          <p>
            Bring nude seamless undergarments, comfortable shoes for walking, a lint roller and one simple alternate
            layer. Photograph your complete outfit ahead of time; I am happy to help you compare options before the
            session.
          </p>
        </section>

        <section className="newborn-article-cta maternity-article-cta">
          <div>
            <p className="newborn-article-kicker">PERSONAL STYLING SUPPORT</p>
            <h2>Want help choosing between outfits?</h2>
            <p>
              Explore the maternity session package, or send me a few outfit photos if you are already booked and want
              help building the final look.
            </p>
          </div>
          <GuideCtaActions category="maternity" />
        </section>
      </div>
    </article>
  </main>
);

export default WhatToWearForMaternityPhotos;
